import { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { isEmpty, debounce, cloneDeep, isArray, isEqual } from "lodash";
import { eachDeep } from "deepdash-es/standalone";
import { treeActions } from "../../../redux/slices/treeSlice";
import { FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { t } from "i18next";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import SourceType from "../../TemplateProperties/SourceType";
import { SectionHeader } from "./SectionHeader";
import { FormattingOptionsSection } from "./OutboundFieldFormattingOptions/FormattingOptionsSection";
import { ProcessingOptionsSection } from "./OutboundFieldFormattingOptions/ProcessingOptionsSection";

import AdvancedProperties from "./Advanced Properties/AdvancedProperties";
import { DatabaseColumn } from "./OutboundFieldFormattingOptions/DatabaseColumn";
import { FieldPropHL7Datatype } from "./FieldPropHL7Datatype";

import { LoadingEase } from "../../LoadingEase";
import { validateAndFixElementName } from "../../../utils/validate";

export default function OutboundFieldPropertyForm({XMLAttribute}) {
  //util hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //redux 
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
  const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
  const templateHeader = rightTree.tree.templateHeader;

  //state declarations
  const [fieldType, setFieldType] = useState(selectedNode?.FIELDTYPE || "ALPHA");
  const [fieldNumber, setFieldNumber] = useState(selectedNode?.fieldNumber || 0); 
  const [description, setDescription] = useState(XMLAttribute ? selectedNode.ATTRIBUTE_NAME || '' : selectedNode?.DESCRIPTION || '');
  const [elementId, setElementId] = useState(selectedNode?.ELEMENT_ID || '');
  const [sourceNodeInfo, setSourceNodeInfo] = useState({FIELDTYPE: "ALPHA"});
  const [fieldPropTopLoaded, setFieldPropTopLoaded] = useState(true);
  const [updatedSelectedNode, setUpdatedSelectedNode] = useState({...selectedNode});
  const [ftClone, setFtClone] = useState(false);
  const [descriptionPrev, setDescriptionPrev] = useState(false);
  const [descriptionErrors, setDescriptionErrors] = useState([]);

  
  const saveNewData = (newNode, sNode, tData) => {
    let nNode = {...newNode};
    let updatedData = eachDeep(cloneDeep(tData), (obj, key, parentArr, ctx) => {
      if (typeof obj !== 'object') return;
      if (obj?.key && obj.key === sNode.key) {
        Object.keys(newNode).forEach((prop) => {
          if (newNode[prop] === "" || newNode[prop] === undefined || newNode[prop] === null) {
            delete obj[prop];
            delete nNode[prop];
          } else {
            if (prop.toUpperCase() === prop || prop === "title") obj[prop] = newNode[prop];
          }
        });
      }
    });

    //prevent unnecessary dispatch
    if (isEqual(tData, updatedData) || isEqual(newNode, sNode)) return;
    
    //set new trans data to updatedData.
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );

    //set selectedNode to match.
    dispatch(
      treeActions.setRightTreeSelectedNode({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: {...nNode, avoidSelection: true},
      })
    );
  }

  const debouncedNodeSave = useCallback(
    debounce((node, sNode, tData) => saveNewData(node, sNode, tData), 25, true), []
  )

  const modifyUpdatedNode = (prop, val, values) => {
    if (val !== "" && val !== undefined) {
      setUpdatedSelectedNode(prev => {
        return prop ? {...prev, [prop]: val} : {...prev, ...values}
      });
    } else {
      setUpdatedSelectedNode(prev => {
        return {...prev, [prop]: null}
      });
    }
  }

  const handleFieldTypeChange = (e) => {
    setFieldType(e?.target?.value || "ALPHA");
    if (e.target.value === "FLOAT") modifyUpdatedNode("ROUND", "TRUE");
    modifyUpdatedNode("FIELDTYPE", e?.target?.value || "ALPHA");

    if (e?.target?.value === "CONSTANT") {
      if (XMLAttribute) {
        modifyUpdatedNode("title", updatedSelectedNode.ATTRIBUTE_NAME);
      } else {
        modifyUpdatedNode("title", `${t("LBL_FIELD")} (${t("CONSTANT")}) ${updatedSelectedNode.DESCRIPTION}`);
      }

      //constant removes source type data.
      setSourceNodeInfo({});
      modifyUpdatedNode("MAPNAME", "");
      modifyUpdatedNode("RECORD_TYPE", "");
      modifyUpdatedNode("SOURCE_TYPE", "");

    } else if (e?.target?.value) {
      if (XMLAttribute) {
        modifyUpdatedNode("title", updatedSelectedNode.ATTRIBUTE_NAME);
      } else {
        modifyUpdatedNode("title", `${t("LBL_FIELD")} ${updatedSelectedNode.DESCRIPTION}`);
      }
    };

    //on filetype change, remove any properties that are not allowed for the new type.
    if (updatedSelectedNode.FIELDTYPE !== e?.target?.value) {
      let dtProps = fieldpropertiesControls[templateHeader.FILETYPE];
      fieldpropertiesControls.ALL.forEach(prop => {
        if (!dtProps[e?.target?.value || "ALPHA"].includes(prop)) {
          if (updatedSelectedNode[prop]) modifyUpdatedNode(prop, null);
        }
      });
    }
    setFtClone(e?.target?.value || "ALPHA");
  }

  const handleDescriptionChange = (newVal) => {
    setDescription(newVal);
    
    if (descriptionErrors.length > 0) {
      setDescriptionErrors([]);
    }
    
    let hasValidationErrors = false;
    
    // Only validate for XML attributes in XML templates
    if (XMLAttribute && templateHeader?.FILETYPE === "XML" && newVal) {
      const validation = validateAndFixElementName(newVal);
      if (!validation.isValid) {
        setDescriptionErrors(validation.errors);
        hasValidationErrors = true;
      }
    }

    if (XMLAttribute) {
      if (!hasValidationErrors) {
        modifyUpdatedNode("ATTRIBUTE_NAME", newVal);
        modifyUpdatedNode("title", newVal);
      }
    } else {
      modifyUpdatedNode("DESCRIPTION", newVal);
      if (updatedSelectedNode.FIELDTYPE === "CONSTANT")  modifyUpdatedNode("title", `${t("LBL_FIELD")} (${t("CONSTANT")}) ${newVal}`);
      else modifyUpdatedNode("title", `${t("LBL_FIELD")} ${newVal}`);
    }
  }

  //blur handler acts like a cleanup filter
  const handleDescriptionBlur = (newVal, e) => {
    setDescriptionErrors([]);
    
    if (XMLAttribute && templateHeader?.FILETYPE === "XML") {
      const validation = validateAndFixElementName(newVal);
      
      if (!validation.isValid && validation.correctedName) {
        setDescription(validation.correctedName);
        modifyUpdatedNode("ATTRIBUTE_NAME", validation.correctedName);
        modifyUpdatedNode("title", validation.correctedName);
        
        if (validation.errors.length > 0) {
          console.warn('Attribute name auto-corrected:', validation.errors);
        }
        return;
      } else if (!validation.isValid && !validation.correctedName) {
        setDescription(descriptionPrev);
        setDescriptionErrors(validation.errors);
        return;
      }
    }
    
    let newDesc = newVal;
    if (newVal === '' || newVal === undefined || newVal === null) {
      if (descriptionPrev !== false && descriptionPrev) {
        if (descriptionPrev !== description) {
          setDescription(descriptionPrev);
          if (XMLAttribute) {
            modifyUpdatedNode("ATTRIBUTE_NAME", descriptionPrev);
            modifyUpdatedNode("title", descriptionPrev);
          } else {
            modifyUpdatedNode("DESCRIPTION", descriptionPrev);
            if (updatedSelectedNode.FIELDTYPE === "CONSTANT")  modifyUpdatedNode("title", `${t("LBL_FIELD")} (${t("CONSTANT")}) ${descriptionPrev}`);
            else modifyUpdatedNode("title", `${t("LBL_FIELD")} ${descriptionPrev}`);
          }
        }
        return;
      }
      
      if (updatedSelectedNode.FIELDTYPE === "CONSTANT") {
        newDesc = getUnnamedMapName(getParentArrayByChildNode(updatedSelectedNode, translatedData), XMLAttribute);  
      } else {
        if (updatedSelectedNode.MAPNAME === '' || updatedSelectedNode.MAPNAME === undefined || updatedSelectedNode.MAPNAME === null) {
          newDesc = getUnnamedMapName(getParentArrayByChildNode(updatedSelectedNode, translatedData), XMLAttribute);  
        } else if (updatedSelectedNode.MAPNAME) {
          newDesc = updatedSelectedNode.MAPNAME;
        }
      }
    }

    if (newDesc !== newVal) {
      setDescription(newDesc);
      if (XMLAttribute) {
        modifyUpdatedNode("ATTRIBUTE_NAME", newDesc);
        modifyUpdatedNode("title", newDesc);
      } else {
        modifyUpdatedNode("DESCRIPTION", newDesc);
        if (updatedSelectedNode.FIELDTYPE === "CONSTANT")  modifyUpdatedNode("title", `${t("LBL_FIELD")} (${t("CONSTANT")}) ${newDesc}`);
        else modifyUpdatedNode("title", `${t("LBL_FIELD")} ${newDesc}`);
      }
    }
  }

  const handleElementIdChange = (newVal) => {
    setElementId(newVal);
    modifyUpdatedNode("ELEMENT_ID", newVal);
  }

  const updatedSelectedNodeWithAdvanceProperties = (val) => {
    modifyUpdatedNode(null, null, val);
  }

  const sourceTypeValueChange = (values, type) => {
    setSourceNodeInfo(values);
    const {PARENT, MAPNAME, RECORD_TYPE, ACTION, INDEX, MAP_REPETITION_INDEX, CUSTOM_DB_FIELD, PLUGIN_NAME, WF_ITEM_ID, WF_ITEM_LOCATION, SOURCE_REPEATING_FIELD } = values;
    let newSelNode = false;

    if (MAPNAME && MAPNAME !== t("SELECT") && type !== "NONE") {
      if (PARENT) modifyUpdatedNode("PARENT", PARENT);
      if (type) modifyUpdatedNode("SOURCE_TYPE", type);
      if (MAPNAME) modifyUpdatedNode("MAPNAME", MAPNAME);
      if (RECORD_TYPE) modifyUpdatedNode("RECORD_TYPE", RECORD_TYPE);
      if (MAP_REPETITION_INDEX) modifyUpdatedNode("MAP_REPETITION_INDEX", MAP_REPETITION_INDEX);
      if (SOURCE_REPEATING_FIELD) modifyUpdatedNode("SOURCE_REPEATING_FIELD", SOURCE_REPEATING_FIELD);
      if (INDEX) modifyUpdatedNode("INDEX", INDEX);
      if (ACTION) modifyUpdatedNode("ACTION", ACTION);
      if (CUSTOM_DB_FIELD) modifyUpdatedNode("CUSTOM_DB_FIELD", CUSTOM_DB_FIELD);
      else modifyUpdatedNode("CUSTOM_DB_FIELD", null);
      if (PLUGIN_NAME) modifyUpdatedNode("PLUGIN_NAME", PLUGIN_NAME);
      else modifyUpdatedNode("PLUGIN_NAME", null);
      if (WF_ITEM_ID) modifyUpdatedNode("WF_ITEM_ID", WF_ITEM_ID);
      else modifyUpdatedNode("WF_ITEM_ID", null);
      if (WF_ITEM_LOCATION) modifyUpdatedNode("WF_ITEM_LOCATION", WF_ITEM_LOCATION);
      else modifyUpdatedNode("WF_ITEM_LOCATION", null);
      if (["TODAY", "TOMORROW", "YESTERDAY"].includes(MAPNAME) && fieldType === "DATE") {
        modifyUpdatedNode("INBOUND_PATTERN", null);
      }
    }
    if (type === "NONE") {
      if (updatedSelectedNode.RECORD_TYPE) modifyUpdatedNode("RECORD_TYPE", null);
      if (updatedSelectedNode.SOURCE_TYPE) modifyUpdatedNode("SOURCE_TYPE", null);
      if (updatedSelectedNode.MAPNAME) modifyUpdatedNode("MAPNAME", null);
      if (MAP_REPETITION_INDEX) modifyUpdatedNode("MAP_REPETITION_INDEX", null);
      if (SOURCE_REPEATING_FIELD) modifyUpdatedNode("SOURCE_REPEATING_FIELD", null);
      if (INDEX) modifyUpdatedNode("INDEX", null);
      if (ACTION) modifyUpdatedNode("ACTION", null);
      if (CUSTOM_DB_FIELD) modifyUpdatedNode("CUSTOM_DB_FIELD", null);
      if (PLUGIN_NAME) modifyUpdatedNode("PLUGIN_NAME", null);
      if (WF_ITEM_ID) modifyUpdatedNode("WF_ITEM_ID", null);
      if (WF_ITEM_LOCATION) modifyUpdatedNode("WF_ITEM_LOCATION", null);
    }

    if (XMLAttribute) {
      if (updatedSelectedNode.ATTRIBUTE_NAME === undefined || updatedSelectedNode.ATTRIBUTE_NAME === "") handleDescriptionBlur("");
    } else {
      if (updatedSelectedNode.DESCRIPTION === undefined || updatedSelectedNode.DESCRIPTION === "") handleDescriptionBlur("");
    }
 }

  const sourceTypeDropdownChange = (val) => {
    if (val !== "INBOUND_DATA") {
      setSourceNodeInfo({});
    }
  }

  useEffect(() => {
    if (!isEqual(selectedNode, updatedSelectedNode)) return debouncedNodeSave(updatedSelectedNode, selectedNode, translatedData); 
  }, [updatedSelectedNode]);

  //case for DATABASE fieltype: if column name is set, and description includes UNNAMED, set description to column name. BG-9907
  useEffect(() => {
    if (
      templateHeader?.FILETYPE === "DATABASE" && 
      updatedSelectedNode?.COLUMN_NAME &&
      description?.includes("UNNAMED")
    ) {
      handleDescriptionChange(updatedSelectedNode?.COLUMN_NAME);
    }
  }, [updatedSelectedNode.COLUMN_NAME]);

  //temporary construction page router to prevent errors while in development
  return (
    <>
      {!isEmpty(rightTree.tree[rightTree.currentTab].selectedNode) ? (
        <div style={styles.fpContainer}>

          {/* Loader shows while states are being set */}
          {!fieldPropTopLoaded && <LoadingEase forceStyles={{padding: "16px 0 8px 16px"}}>{t('LOADING')} {t('LBL_FLD_PROP')}...</LoadingEase>}
          
          <div className="width-100" style={{display: fieldPropTopLoaded ? "unset" : "none"}}>
            <div className="flex flex-wrap" style={{alignItems: "flex-start", padding: "0 8px 0 14px"}}>
              {templateHeader.FILETYPE === "DATABASE" && (
                <DatabaseColumn updatedSelectedNode={updatedSelectedNode} setUpdatedSelectedNode={setUpdatedSelectedNode}/>
              )}

              {/* Field Type - Dropdown */}
              <FormControl variant="outlined" sx={{marginRight: "8px", minWidth: "150px", width: "unset", marginTop: "16px", height: "35px", flex: "1"}} size="small">
                <InputLabel id="ftDropdownLabel">{t("LBL_FLD_TYPE").replace(":", "")}</InputLabel>
                <Select
                  sx={{height: "34px", width: "unset"}}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  }}
                  variant="outlined"
                  size="small"
                  name={"FIELDTYPE"}
                  value={fieldType}
                  labelId="ftDropdownLabel"
                  label={t("LBL_FLD_TYPE").replace(":", "")}
                  id="ftDropdown"
                  onChange={handleFieldTypeChange}
                  // style={{height: "35px"}}
                  disabled={!fieldPropTopLoaded}
                >
                  {fieldTypeOptionsDropdown}
                </Select>
              </FormControl>
              
              {/* Description - Text Input */}
              <FormControl  size="small" sx={{flex: "1", minWidth: "110px", marginTop: "16px", marginRight: "8px"}}>
                <TextField
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                  label={XMLAttribute ? t("LBL_ATTR_NAME").replace(":", "")  : t("LBL_DESC").replace(":", "")}
                  className={"width-100"}
                  name={"DESCRIPTION"}
                  value={description}
                  disabled={!fieldPropTopLoaded}
                  error={XMLAttribute && templateHeader?.FILETYPE === "XML" && descriptionErrors.length > 0}
                  helperText={XMLAttribute && templateHeader?.FILETYPE === "XML" && descriptionErrors.length > 0 ? descriptionErrors[0] : ''}
                  onChange={(e) => {
                    return handleDescriptionChange(e.target.value, e)
                  }}
                  onBlur={(e) => {
                    return handleDescriptionBlur(e.target.value, e)
                  }}
                  onFocus={(e) => {
                    setDescriptionPrev(e.target.value);
                  }}
                />
              </FormControl>
            </div>

            {(templateHeader?.FILETYPE === "EDI") && (
              <div style={{ padding: "12px 16px 0 14px" }}>
                {/* Element Id - Text Input */}

                <FormControl size="small" sx={{ width: "100%", minWidth: "110px", marginRight: "8px" }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    autoComplete="off"
                    label={t("MAP_ELEMENT_ID")}
                    className={"width-100"}
                    name={"MAP_ELEMENT_ID"}
                    value={elementId}
                    disabled={!fieldPropTopLoaded}
                    onChange={(e) => {
                      handleElementIdChange(e.target.value, e)
                    }}
                  // onFocus={handleDescFocus}
                  />
                </FormControl>

              </div>
            )}

            {selectedNode && templateHeader && templateHeader.FILETYPE === "HL7" && ["ALPHA", "CONSTANT", "DATE", "FLOAT", "INTEGER"].includes(selectedNode?.FIELDTYPE) && (
              <div style={{fontSize: "12px", margin: "12px 16px 0 14px"}}>
                <FieldPropHL7Datatype/>
              </div>
            )}

            {/* Field Number - Uneditable Textbox */}
            {!(XMLAttribute && templateHeader?.FILETYPE === "XML") && 
              <div style={{color: "#666666", fontSize: "12px", margin: "8px 16px 0 14px"}}>
                <span style={{marginRight: "12px"}}>{t("LBL_FLD_NUM")}</span>
                <span >{fieldNumber}</span>
              </div>
            }

          </div>  
                
          {/* Data Source Section */}
          {(selectedNode?.FIELDTYPE && selectedNode.FIELDTYPE !== "CONSTANT" && 
            !(templateHeader?.FILETYPE === "XML" && (selectedNode.FIELDTYPE === "PACKED_INT" || selectedNode.FIELDTYPE === "ZONED_INT"))) && 
            <Accordion sx={styles.accordianContainer} defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="accordianDataSource"
                className="accordian-override"
                id="accordianDataSource"
                sx={{...styles.accordianHeader, minHeight: "50px !important", height: "50px !important"}}
              >
                <SectionHeader text={t("LBL_DATA_SOURCE")} />
              </AccordionSummary>
              <AccordionDetails sx={{...styles.accordianContentFO}}>
                {/* <DataSourceSection /> */}
                <SourceType
                  // node={{
                  //   PARENT: selectedNode.PARENT && selectedNode.RECORD_TYPE ? selectedNode.PARENT+'\u001e'+selectedNode.RECORD_TYPE 
                  //     : selectedNode.PARENT ? selectedNode.PARENT 
                  //     : selectedNode.RECORD_TYPE,
                  //   RECORD_TYPE: selectedNode.RECORD_TYPE,
                  //   MAPNAME: selectedNode.MAPNAME ,
                  //   key: selectedNode.key
                  // }}
                  node={{
                    PARENT: selectedNode?.PARENT,
                    MAPNAME: selectedNode?.MAPNAME,
                    RECORD_TYPE: selectedNode?.RECORD_TYPE,
                    key: selectedNode?.key,
                    ACTION: selectedNode?.ACTION,
										INDEX: selectedNode?.INDEX,
                    MAP_REPETITION_INDEX: selectedNode?.MAP_REPETITION_INDEX,
                    CUSTOM_DB_FIELD: selectedNode?.CUSTOM_DB_FIELD,
                    PLUGIN_NAME: selectedNode?.PLUGIN_NAME
                  }}
                  valuesHandler={sourceTypeValueChange}
                  initialSelectCall={true} // Initial call required so added this props
                  sourceTypeDropdownChange={sourceTypeDropdownChange}
                  // callerComponent='recordProperties'
                />
              </AccordionDetails>
            </Accordion>
          }
          

          {/* Formatting Options Section */}
          {!(templateHeader?.FILETYPE === "XML" && (selectedNode?.FIELDTYPE === "PACKED_INT" || selectedNode?.FIELDTYPE === "ZONED_INT")) && 
            <Accordion sx={styles.accordianContainer} defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="accordianFormattingOptions"
                id="accordianFormattingOptions"
                className="accordian-override"
                sx={{...styles.accordianHeader, minHeight: "50px !important", height: "50px !important"}}
              >
                <SectionHeader text={t("LBL_FORMATE_OPTIONS")} />
              </AccordionSummary>
              <AccordionDetails sx={styles.accordianContentFO}>
                <FormattingOptionsSection 
                  updatedSelectedNode={updatedSelectedNode} 
                  setUpdatedSelectedNode={setUpdatedSelectedNode}
                  sourceNodeInfo={sourceNodeInfo}
                  ftClone={ftClone}
                />
              </AccordionDetails>
            </Accordion>
          }

          {!(templateHeader?.FILETYPE === "XML" && (selectedNode?.FIELDTYPE === "PACKED_INT" || selectedNode?.FIELDTYPE === "ZONED_INT")) && 
            <div style={{padding: "10px 15px 0 15px"}}>
              {/* Advanced Properties */}
              <AdvancedProperties selectedNode={selectedNode} updatedSelectedNodeWithAdvanceProperties={updatedSelectedNodeWithAdvanceProperties} />
            </div>
          }

          {!(templateHeader?.FILETYPE === "XML" && (selectedNode?.FIELDTYPE === "PACKED_INT" || selectedNode?.FIELDTYPE === "ZONED_INT")) && 
            <Accordion sx={styles.accordianContainer} defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="accordianProcessingOptions"
                id="accordianProcessingOptions"
                className="accordian-override"
                sx={{...styles.accordianHeader, minHeight: "50px !important", height: "50px !important"}}
              >
                <SectionHeader text={t("LBL_PROCESSING_OPTIONS").replace(":", "")} />
              </AccordionSummary>
              <AccordionDetails sx={styles.accordianContentFO}>
                <ProcessingOptionsSection updatedSelectedNode={updatedSelectedNode} setUpdatedSelectedNode={setUpdatedSelectedNode}/>
              </AccordionDetails>
            </Accordion>
          }
          
        </div>
      ) : (
        <div className="height-100">
          <div className="panel-no-data">{t('NO_FLD_SELECTED')}</div>
        </div>
      )}
    </>
  );
}

//field type dropdown options with constant always included.
const fieldTypeOptionsArray = [
  { value: "SELECT", text: "SELECT" },
  { value: "ALPHA", text: "ALPHA" },
  { value: "BIT_FLAG", text: "BIT_FLAG" },
  { value: "CONSTANT", text: "CONSTANT" },
  { value: "DATE", text: "DATE" },
  { value: "FLOAT", text: "FLOAT" },
  { value: "INTEGER", text: "INTEGER" },
  { value: "PACKED_INT", text: "PACKED_INT" },
  { value: "ZONED_INT", text: "ZONED_INT" },
];

const fieldTypeOptionsDropdown = [
  <MenuItem key={1} value={"SELECT"}>{t("SELECT")}</MenuItem>,
  <MenuItem key={2} value={"ALPHA"}>{t("ALPHA")}</MenuItem>,
  <MenuItem key={3} value={"BIT_FLAG"}>{t("BIT_FLAG")}</MenuItem>,
  <MenuItem key={4} value={"CONSTANT"}>{t("CONSTANT")}</MenuItem>,
  <MenuItem key={5} value={"DATE"}>{t("DATE")}</MenuItem>,
  <MenuItem key={6} value={"FLOAT"}>{t("FLOAT")}</MenuItem>,
  <MenuItem key={7} value={"INTEGER"}>{t("INTEGER")}</MenuItem>,
  <MenuItem key={8} value={"PACKED_INT"}>{t("PACKED_INT")}</MenuItem>,
  <MenuItem key={9} value={"ZONED_INT"}>{t("ZONED_INT")}</MenuItem>,
];


//UTIL FUNCTIONS BELOW

//determine what to name an unnamed node, finding 
//  the lowest avail number to append to string 'UNNAMED'
export const getUnnamedMapName = (parentArray, XMLAttribute) => {
  let unnamedCount = 0;
  let unnamedArr = [];
  let desc = XMLAttribute ? "ATTRIBUTE_NAME" : "DESCRIPTION";
  const data = cloneDeep(parentArray);
  const getMapName = (data) => {
    data.forEach((ele) => {
      if ((ele[desc] && ele[desc].startsWith(t("UNNAMED")))) {
        const latestUnnamedDescription = +ele[desc].slice(t("UNNAMED").length);
        if (!isNaN(latestUnnamedDescription)) unnamedArr.push(latestUnnamedDescription);
      } else if (ele.MAPNAME && ele.MAPNAME.startsWith(t("UNNAMED"))) {
        const latestUnnamedMapName = +ele.MAPNAME.slice(t("UNNAMED").length);
        if (!isNaN(latestUnnamedMapName)) unnamedArr.push(latestUnnamedMapName);
      } else if (ele.title) {
        if (ele.title.startsWith(`${t("LBL_FIELD")} ${t("UNNAMED")}`)) {
          const latestUnnamedMapName = +ele.title.replace(`${t("LBL_FIELD")} `, "").slice(t("UNNAMED").length);
          if (!isNaN(latestUnnamedMapName)) unnamedArr.push(latestUnnamedMapName);
        } else if (ele.title.startsWith(`${t("LBL_FIELD")} (${t("CONSTANT")}) ${t("UNNAMED")}`)) {
          const latestUnnamedMapName = +ele.title.replace(`${t("LBL_FIELD")} `, "").slice(t("UNNAMED").length);
          if (!isNaN(latestUnnamedMapName)) unnamedArr.push(latestUnnamedMapName);
        }
      }
      if (ele?.children?.length > 0) {
        getMapName(ele.children);
      }
    });
  };

  getMapName(data);
  const findLowestSequentialNumberUnique = (arr) => {
    // Remove duplicates and sort the array to ensure it is in ascending order
    const uniqueSortedArr = [...new Set(arr)].sort((a, b) => a - b);
  
    // Start from the first element and check sequentiality
    for (let i = 0; i < uniqueSortedArr.length - 1; i++) {
      // If the next element is not sequential, return the current element
      if (uniqueSortedArr[i] + 1 !== uniqueSortedArr[i + 1]) {
        return uniqueSortedArr[i];
      }
    }
  
    // If all elements are sequential, return the last element
    return uniqueSortedArr[uniqueSortedArr.length - 1];
  }
  if ([...new Set(unnamedArr)].length > 1) {
    unnamedCount = 1 + findLowestSequentialNumberUnique(unnamedArr);
  }else if ([...new Set(unnamedArr)].length === 1) {
    unnamedCount = 1;
  }else {
    unnamedCount = "";
  }
  return `${t("UNNAMED")}${unnamedCount}`;
};

//convert delim text bewtween value and visible character for user.
export const delimDisplay = (val, modal) => {
  // Display values in certain ways depending on a few specific conditions:
  // - If no delim val -> 'No Delimiter Chosen'.
  // - Replace 'CR' and 'LF' (ascii 13 and 10) OR '%0D' '%0A' -> display 'CR', 'LF', 'CRLF' in latin characters.
  // - If val is one or two char length -> display 'ASCII 42 (*)' delimited by comma.
  // - else -> display the decoded string (even if it's just empty chars ?)
  if (val === undefined) return '';
  if (!modal) {
    if (typeof val === 'boolean' || val === t('TXT_NO_DELIM_CHOSEN') || !val) return t('TXT_NO_DELIM_CHOSEN');
  }
  if (val === "CR" || val === "%0D" || val === "\r") return "CR";
  if (val === "LF" || val === "%0A" | val === "\n") return "LF";
  if (val === "CRLF" || val === "%0D%0A") return "CRLF";

  try { val = decodeURIComponent(val) }
  catch (e) {
    //case - malformed URI
    //console.log(`delimDisplay - URI malformed, can not decode - ${props.label} '${val}' - Displaying raw.`) 
  }
  return val.length < 3 ?
    val.split('').map(e => `ASCII ${e.charCodeAt(0)} (${e})`).join(', ')
    : val;
}

//returns array of nodes that are children to the passed in parent node.
export const getParentArray = (parentKey, translatedData) => {
  let nestedNodes = parentKey.split("-");
  nestedNodes = nestedNodes?.splice(1);

  const data = cloneDeep(translatedData);
  let finalArray = [];

  const getArray = (translatedData, nodes) => {
    if (nodes?.length === 0) {
      const { children } = translatedData;
      finalArray = children ? children : translatedData;
      return;
    }

    const isArray = Array.isArray(translatedData);
    if (isArray) {
      const nestedData = translatedData[[nodes?.[0]]];
      const newNestedNodes = nodes?.splice(1);
      getArray(nestedData, newNestedNodes);
    } else getArray(translatedData?.children, nodes);
  };
  getArray(data, nestedNodes);
  return finalArray;
};

//returns array of nodes that are children to the parent node of the passed child node. pass in node.pos and latest translatedData.
export const getParentArrayByChildNode = (node, translatedData) => {
  let parentArr = [];

  eachDeep(translatedData, (value, key, pArr) => {
    if (typeof value !== "object") return;
    if (parentArr.length > 0) return;
    if (value.children && value.children.length > 0) {
      value.children.forEach((child) => {
        if (child.key === node.key) {
          parentArr = value.children;
          return;
        }
      })
    }
  })

  return parentArr;
};

// drill through tdata to find node by key - parent of node passed in.
const getParentNode = (selectedNode, translatedData) => {
  let node;
  const parentKey = selectedNode.key.split("-").slice(0, -1).join("-")
  eachDeep(cloneDeep(translatedData), (obj) => {
    if (obj && obj.key && obj.key === parentKey) {
      node = obj
      return false;
    }
    if (isArray(obj?.children)) {
      return obj.children;
    }
  })
  return node;
}

const styles = {
  fpContainer: {
    // padding: "0 16px 16px 16px"
    padding: "0 4px 4px 4px"
  },
  accordianContainer: {
    border: "none",
    padding: "0",
    margin: "0",
    boxShadow: "none",
    backgroundColor: "unset"
  },
  accordianHeader: {
    border: "none",
    padding: "0",
    margin: "0",
    backgroundColor: "unset"
  },
  accordianContent: {
    border: "none",
    padding: "0 15px 0 15px",
    margin: "0",
    backgroundColor: "unset"
  },
  accordianContentFO: {
    border: "none",
    padding: "0 15px",
    margin: "0",
    backgroundColor: "unset"
  },
}

//control options for each file + field type. used for remove/add when field type changes.
const fieldpropertiesControls = {
  "ALL": [
    "CONVERT_CASE",
    "PADDIR",
    "FILLCHAR",
    "LENGTH",
    "TRIMDATA",
    "QUOTE_DATA",
    "URLENCODE",
    "SUPPRESS_DELIMITER",
    "IGNORE_GLOBAL_SUB",
    "RPT_TRUNC_ERR",
    "KEEP_ORIG_DATA_ON_ERROR",
    "DEFAULT_VALUE",
    "VALUE",
    "REPEAT",
    "INBOUND_PATTERN",
    "PATTERN",
    "TIMEZONE",
    "PRECISION",
    "ROUND",
    "PRECISION_OUT",
    "DEC_PATTERN",
    "UNSIGNED",
    "REQUIRED",
    "CONVERT_EMPTY",
    "CONVERT_NULL",
    "INCLUDE_NULL",
    "FIELD_ID",
    "REPEATING",
    "ESCAPE_DATA",
    "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER",
    "ESCAPE_XML_DATA",
  ],
  "CSV": {
    "ALPHA": [
      //field props
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      //checkboxes
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
  },
  "DATABASE": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION","KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ROUND", "RPT_TRUNC_ERR"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL","RPT_TRUNC_ERR"
    ],
  },
  "DELIMITED": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB","RPT_TRUNC_ERR"
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
  },
  "DELIM_FLD_ID": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH", "FIELD_ID",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB","RPT_TRUNC_ERR", "REPEATING",
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
  },
  "EDI": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR",
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB","RPT_TRUNC_ERR",
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", 
    ],
  },
  "FIXED_LENGTH": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH", 
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR",
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB","RPT_TRUNC_ERR",
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", 
    ],
  },
  "HL7": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR",
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB","RPT_TRUNC_ERR",
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", 
    ],
  }, 
  "JSON": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "ROUND", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
  },
  "MULIT_DELIMITED": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB","RPT_TRUNC_ERR", "REPEATING",
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
  },
  "SERVLET": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
  },
  "XML": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "ESCAPE_XML_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "RPT_TRUNC_ERR"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "ESCAPE_XML_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "RPT_TRUNC_ERR"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "ESCAPE_XML_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "RPT_TRUNC_ERR"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "ESCAPE_XML_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "RPT_TRUNC_ERR"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "ESCAPE_XML_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "ROUND", "RPT_TRUNC_ERR"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "ESCAPE_XML_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "RPT_TRUNC_ERR"
    ],
    "PACKED_INT": [],
    "ZONED_INT": [],
  },
}


//
const fs = [
  "SELECT", "ALPHA", "BIT_FLAG", 
  "CONSTANT", "DATE", "FLOAT", "INTEGER", 
  "PACKED_INT", "ZONED_INT"
];